import { z } from 'zod';

type SerializedDef = {
  typeName?: string;
  values?: unknown[];
  value?: unknown;
  type?: unknown;
};

type SerializedZod = {
  _def?: SerializedDef;
};

/**
 * Resolves a Zod schema from a JSON schema. Used to hydrate from saved file.
 *
 * @param schema serialized zod schema object (unknown at runtime)
 * @returns z.ZodSchema
 */
export const parseZodSchema = (schema: unknown): z.ZodSchema => {
  if (!schema || typeof schema !== 'object') {
    return z.any();
  }

  const s = schema as SerializedZod;
  const definition = s._def;

  if (!definition || !definition.typeName) {
    return z.any();
  }

  switch (definition.typeName) {
    case 'ZodAny':
      return z.any();
    case 'ZodEnum':
      if (Array.isArray(definition.values) && definition.values.every((v) => typeof v === 'string')) {
        return z.enum(definition.values as [string, ...string[]]);
      }
      return z.any();
    case 'ZodLiteral':
      return z.literal(definition.value as string | number | boolean | null);
    case 'ZodString':
      return z.string();
    case 'ZodNumber':
      return z.number();
    case 'ZodBoolean':
      return z.boolean();
    case 'ZodArray':
      return z.array(parseZodSchema(definition.type));
    default:
      return z.any();
  }
};

/**
 * This function converts event based javascript file reader to promise based.
 * It uses readAsBinaryString so the format is supported by the template engine API.
 *
 * @param file Object which represents the example document used to generate the template
 * @returns string representation of the file
 */
export const fileToText = (file: File) => {
  return new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const result = fr.result;

      if (typeof result === 'string') {
        resolve(result);
      } else if (result instanceof ArrayBuffer) {
        resolve(arrayBufferToBinaryString(result));
      } else {
        reject('Error reading file');
      }
    };
    fr.onerror = reject;
    fr.readAsArrayBuffer(file);
  });
};

const arrayBufferToBinaryString = (buffer: ArrayBuffer) => {
  let binaryString = '';
  const bytes = new Uint8Array(buffer);
  const length = bytes.length;
  for (let i = 0; i < length; i++) {
    binaryString += String.fromCharCode(bytes[i] as number);
  }
  return binaryString;
};
